import React, { createContext, useContext, useState, useEffect } from 'react';
import { useResponsiveBreakpoint } from '@/hooks/use-responsive-breakpoint';

interface SidebarContextType {
  collapsed: boolean;
  setCollapsed: (collapsed: boolean) => void;
  effectiveBreakpoint: 'mobile' | 'tablet' | 'tablet-collapsed' | 'desktop';
  sidebarWidth: number;
  availableContentWidth: number;
  headerHeight: number;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const breakpoint = useResponsiveBreakpoint();
  const [collapsed, setCollapsed] = useState(breakpoint === 'tablet');

  // Update collapsed state when breakpoint changes
  useEffect(() => {
    if (breakpoint === 'tablet') {
      setCollapsed(true);
    }
  }, [breakpoint]);

  // Calculate effective breakpoint considering sidebar state
  const effectiveBreakpoint = React.useMemo(() => {
    if (breakpoint === 'mobile') return 'mobile';
    if (breakpoint === 'tablet') {
      return collapsed ? 'tablet-collapsed' : 'tablet';
    }
    return 'desktop';
  }, [breakpoint, collapsed]);

  // Calculate sidebar width and available content width
  const { sidebarWidth, availableContentWidth, headerHeight } = React.useMemo(() => {
    const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1024;
    const header = 64; // 16 * 4 = 64px (h-16)
    
    let sidebar = 0;
    if (breakpoint !== 'mobile') {
      sidebar = collapsed ? 80 : (breakpoint === 'tablet' ? 240 : 288);
    }
    
    return {
      sidebarWidth: sidebar,
      availableContentWidth: screenWidth - sidebar,
      headerHeight: header
    };
  }, [breakpoint, collapsed]);

  return (
    <SidebarContext.Provider 
      value={{ 
        collapsed, 
        setCollapsed, 
        effectiveBreakpoint,
        sidebarWidth,
        availableContentWidth,
        headerHeight
      }}
    >
      {children}
    </SidebarContext.Provider>
  );
};